import * as React from 'react';
import { Box, Dialog, Slide } from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';
import ImageList from '@mui/material/ImageList';
import ImageListItem from '@mui/material/ImageListItem';
import Typography from '@mui/material/Typography';
import FileIcon from '@mui/icons-material/DescriptionOutlined';
import MyImageGallery from '../global/ImageGallery';
import { FileSectionRow } from '../../interfaces';
import { openExternalUrl } from '../../utils/global/helper';
import useResolvedFileUrls from '../../hooks/global/useResolvedFileUrls';

type Props = {
  label: string;
  values: FileSectionRow['value'];
  compact?: boolean;
};

const Transition = React.forwardRef(
  (
    props: TransitionProps & {
      children: React.ReactElement;
    },
    ref: React.Ref<unknown>,
    // eslint-disable-next-line react/jsx-props-no-spreading
  ) => <Slide direction="up" ref={ref} {...props} />,
);

export default function SectionFileRow({ label, values, compact }: Props) {
  const [galleryOpen, setGalleryOpen] = React.useState(false);
  const [currentImageIndex, setCurrentImageIndex] = React.useState<number>(0);

  const { getDisplayUrl } = useResolvedFileUrls(values);
  const images = values.filter(file => file.type === 'image');

  const handleClickOpen = (index: number) => {
    const file = values[index];
    const displayUrl = getDisplayUrl(file.url);

    if (file.type === 'pdf') {
      openExternalUrl(displayUrl);
      return;
    }

    setGalleryOpen(true);
    const imageIndex = images.findIndex(image => image.url === file.url);
    setCurrentImageIndex(imageIndex);
  };

  const handleClose = () => {
    setGalleryOpen(false);
  };

  const galleryImageUrls = React.useMemo(
    () => images.map(file => getDisplayUrl(file.url)),
    [images, getDisplayUrl],
  );

  const renderItemContent = (file: FileSectionRow['value'][number]) => {
    const displayUrl = getDisplayUrl(file.url);
    return (
      <>
        {file.type === 'image' && (
          <img
            src={displayUrl}
            alt="img"
            style={{
              objectFit: 'cover',
              width: '100%',
              height: '100%',
            }}
          />
        )}
        {file.type === 'pdf' && (
          <Box
            width="100%"
            height="100%"
            display="flex"
            alignItems="center"
            justifyContent="center">
            <FileIcon sx={{ fontSize: compact ? '1.5rem' : '3rem' }} />
          </Box>
        )}
        {file.actionComponent && (
          <Box
            position="absolute"
            top={theme => theme.spacing(1)}
            right={theme => theme.spacing(1)}>
            {file.actionComponent}
          </Box>
        )}
        {file.showName && (
          <Box
            position="absolute"
            bottom={0}
            left={0}
            right={0}
            p={1}
            bgcolor="rgba(0, 0, 0, 0.5)">
            <Typography
              title={file.name}
              variant="caption"
              display="block"
              color="white"
              textAlign="center"
              fontWeight={600}
              sx={{
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
              }}>
              {file.name}
            </Typography>
          </Box>
        )}
      </>
    );
  };

  return (
    <Box className={compact ? undefined : 'section-row'} width="100%">
      {compact ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '7px' }}>
          <Typography
            sx={{
              fontSize: 'var(--sidebar-font-xs)',
              fontWeight: 700,
              letterSpacing: '0.04em',
              color: 'var(--sidebar-tertiary)',
            }}>
            {label}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {values.map((file, index) => (
              <Box
                key={file.id}
                onClick={() => handleClickOpen(index)}
                sx={{
                  position: 'relative',
                  width: 64,
                  height: 64,
                  flexShrink: 0,
                  borderRadius: '5px',
                  border: '1px solid var(--sidebar-border-light)',
                  overflow: 'hidden',
                  cursor: 'pointer',
                }}>
                {renderItemContent(file)}
              </Box>
            ))}
          </Box>
        </Box>
      ) : (
        <>
          <Typography variant="overline" component="p">
            {label}
          </Typography>
          <ImageList cols={3} rowHeight={164} sx={{ position: 'relative' }}>
            {values.map((file, index) => (
              <ImageListItem
                key={file.id}
                sx={{
                  position: 'relative',
                  borderRadius: '10px',
                  bgcolor: theme => theme.palette.lightGrey.main,
                  overflow: 'hidden',
                  cursor: 'pointer',
                }}
                onClick={() => handleClickOpen(index)}>
                {renderItemContent(file)}
              </ImageListItem>
            ))}
          </ImageList>
        </>
      )}
      <Dialog
        fullScreen
        open={galleryOpen}
        onClose={handleClose}
        TransitionComponent={Transition}
        className="gallery-modal">
        <MyImageGallery
          images={galleryImageUrls}
          closeGallery={handleClose}
          currentIndex={currentImageIndex}
        />
      </Dialog>
    </Box>
  );
}
